import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, GradeBadge, FrameworkNote } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, Printer, ArrowLeft, Clock, Award, BookOpen, ClipboardCheck, ScrollText } from "lucide-react";
import { toast } from "sonner";

export const PublicShell = ({ children }) => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen paper-noise">
      <header className="border-b border-border bg-[#FBFAF4]/90 backdrop-blur print:hidden">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 font-display font-semibold text-lg" data-testid="public-brand">
            <span className="h-9 w-9 rounded-xl bg-[#0F7B7D] text-white flex items-center justify-center"><GraduationCap className="h-5 w-5" /></span>
            Scholar Campus
          </Link>
          <nav className="ml-auto flex items-center gap-1 text-sm">
            <Button variant="ghost" asChild><Link to="/syllabi" data-testid="public-nav-syllabi">Syllabi</Link></Button>
            <Button variant="ghost" asChild><Link to="/policies" data-testid="public-nav-policies">Policies</Link></Button>
            <Button asChild><Link to={user ? "/dashboard" : "/login"} data-testid="public-nav-cta">{user ? "Dashboard" : "Sign in"}</Link></Button>
          </nav>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="mt-8">
    <h2 className="flex items-center gap-2 text-lg font-display font-semibold"><Icon className="h-5 w-5 text-[#0B6B6B]" />{title}</h2>
    <div className="mt-2 text-[#123645] leading-relaxed">{children}</div>
  </section>
);

function AdminCreditEditor({ s, onSaved }) {
  const [credits, setCredits] = useState(String(s.credits));
  const [unit, setUnit] = useState(s.credit_unit);
  const [cat, setCat] = useState(s.tx_category);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/records/meta").then(({ data }) => setCats(data.tx_categories)); }, []);
  const save = async () => {
    try {
      await api.patch(`/admin/courses/${s.course_id}/credits`, { credits: parseFloat(credits), credit_unit: unit, tx_category: cat });
      toast.success("Course credit updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 rounded-xl border border-dashed border-[#0F7B7D] p-4 print:hidden" data-testid="syllabus-admin-credit">
      <div className="text-sm font-medium mb-3">Admin: course credit policy</div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Credit value</Label><Input type="number" step="0.5" min="0" max="6" value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="syllabus-credit-input" /></div>
        <div><Label className="text-xs">Unit</Label>
          <Select value={unit} onValueChange={setUnit}><SelectTrigger data-testid="syllabus-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="credits">High school credits</SelectItem><SelectItem value="semester hours">Semester hours</SelectItem><SelectItem value="non-credit">Non-credit</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Texas category</Label>
          <Select value={cat} onValueChange={setCat}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={save} data-testid="syllabus-credit-save">Save</Button>
      </div>
    </div>
  );
}

export default function Syllabus() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get(`/public/courses/${id}/syllabus`).then(({ data }) => setS(data)).catch((e) => setErr(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (err) return <PublicShell><div className="text-center py-20" data-testid="syllabus-error"><h1 className="text-2xl font-display font-semibold">Syllabus not found</h1><p className="text-muted-foreground mt-2">{err}</p></div></PublicShell>;
  if (!s) return <PublicShell><Loading /></PublicShell>;
  const creditText = s.credit_unit === "non-credit" ? "Non-credit course" : `${s.credits} ${s.credit_unit === "semester hours" ? "semester hours" : "high school credit"}`;
  return (
    <PublicShell>
      <div className="flex gap-2 mb-4 print:hidden">
        <Button variant="ghost" asChild className="-ml-3"><Link to="/syllabi"><ArrowLeft className="h-4 w-4 mr-1" />All syllabi</Link></Button>
        <Button variant="outline" className="ml-auto" onClick={() => window.print()} data-testid="syllabus-print-button"><Printer className="h-4 w-4 mr-2" />Print</Button>
      </div>
      <article className="bg-white border border-border rounded-[18px] p-6 sm:p-10 print:border-0 print:p-0" data-testid="syllabus-document">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-display">Course syllabus</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="syllabus-title">{s.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <GradeBadge grade={s.grade_band} /><span>{s.subject}</span><span>· {s.collection === "Core" || !s.collection ? "Core program" : s.collection}</span>
        </div>
        <p className="mt-4 text-lg text-[#123645] max-w-[72ch]">{s.description}</p>
        <FrameworkNote item={s} className="mt-4" />

        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">Credit</div><div className="font-semibold" data-testid="syllabus-credit">{creditText}</div><div className="text-xs text-muted-foreground">Texas category: {s.tx_category}</div></div>
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">Estimated time</div><div className="font-semibold">{s.estimated_hours} hours</div><div className="text-xs text-muted-foreground">{s.lessons.length} lessons with quizzes</div></div>
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">Standards covered</div><div className="font-semibold">{s.standards.length} codes</div><div className="text-xs text-muted-foreground">Listed per lesson below</div></div>
        </div>
        {user?.role === "admin" && <AdminCreditEditor s={s} onSaved={load} />}

        <Section icon={BookOpen} title="Course outline">
          <ol className="space-y-4" data-testid="syllabus-outline">
            {s.lessons.map((l, i) => (
              <li key={l.lesson_id} className="rounded-xl border border-border p-4">
                <div className="font-medium">Lesson {i + 1}: {l.title}</div>
                {l.summary && <div className="text-sm text-muted-foreground mt-1">{l.summary}</div>}
                {l.objectives?.length > 0 && <ul className="mt-2 text-sm list-disc pl-5 space-y-0.5">{l.objectives.map((o) => <li key={o}>{o}</li>)}</ul>}
                {l.standards?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">{l.standards.map((c) => <span key={c} className="max-w-full break-words px-2 py-0.5 rounded-md bg-[#D7F3F1] text-xs font-mono text-[#0B6B6B]">{c}</span>)}</div>
                )}
                <div className="mt-2 text-xs text-muted-foreground">Assessment: {l.quiz_questions}-question mastery quiz</div>
              </li>
            ))}
          </ol>
        </Section>
        <Section icon={ClipboardCheck} title="Assessment & grading"><p>{s.assessment}</p><p className="mt-2">{s.grading}</p></Section>
        <Section icon={Clock} title="Attendance & time on task"><p>{s.attendance}</p></Section>
        <Section icon={Award} title="Materials & support"><p>{s.materials}</p></Section>
        <Section icon={ScrollText} title="School policies"><p>Grading, attendance, academic integrity, privacy and AI-use policies apply to this course. <Link to="/policies" className="text-[#0B6B6B] underline">Read the school policies</Link>.</p></Section>
      </article>
    </PublicShell>
  );
}

const PROGRAMS = [
  ["Core", "Core courses"], ["Texas TEKS", "Texas TEKS & STAAR"], ["AP", "AP exam prep"], ["SAT & ACT", "SAT & ACT prep"], ["GED", "GED prep"], ["Professional", "Professional certification"],
];
const GRADES = ["Pre-K", "K-2", "3-5", "6-8", "9-12", "College"];

export function SyllabiIndex() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/public/courses").then(({ data }) => setRows(data)); }, []);
  const grouped = useMemo(() => {
    const g = {};
    (rows || []).forEach((c) => {
      const p = c.collection || "Core";
      g[p] = g[p] || {};
      (g[p][c.grade_band] = g[p][c.grade_band] || []).push(c);
    });
    return g;
  }, [rows]);
  return (
    <PublicShell>
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-display">Scholar Campus</div>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="syllabi-title">Course syllabi</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">Every starter course has a public syllabus with its outline, the standards each lesson covers, credit value, assessment and grading.</p>
      {!rows ? <Loading /> : PROGRAMS.filter(([k]) => grouped[k]).map(([k, label]) => (
        <section key={k} className="mt-10" data-testid={`syllabi-program-${k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          <h2 className="text-xl font-display font-semibold">{label}</h2>
          {GRADES.filter((g) => grouped[k][g]).map((g) => (
            <div key={g} className="mt-4">
              <div className="text-sm font-medium text-muted-foreground mb-2">{g}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {grouped[k][g].map((c) => (
                  <Link key={c.course_id} to={`/syllabus/${c.course_id}`} className="rounded-xl border border-border bg-white px-4 py-3 hover:border-[#0F7B7D] transition-colors duration-150" data-testid="syllabi-link">
                    <div className="font-medium">{c.title}</div><div className="text-xs text-muted-foreground">{c.subject}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </PublicShell>
  );
}
